import React, { useState, useEffect } from 'react';
import './LandingPage.css';
import {
  Lock,
  Mail,
  Shield,
  Eye,
  Clock,
  ArrowRight,
  ChevronDown,
  Zap,
  Users,
  Globe,
  X,
} from 'lucide-react';

function LandingPage() {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [outlookIcon, setOutlookIcon] = useState(null);
  const [demoSrc, setDemoSrc] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [showScroll, setShowScroll] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState('');
  const SCROLL_THRESHOLD = 200; // px to show the scroll-top button
  // screenshot gallery removed per request

  const features = [
    {
      icon: Lock,
      title: 'Bank-Grade Encryption',
      description: 'RSA + AES end-to-end encryption for messages and attachments',
    },
    {
      icon: Shield,
      title: 'Identity Verification',
        description: 'OTP verification via email ensures only intended recipient can view',
    },
    {
      icon: Eye,
      title: 'Secure Access Control',
      description: 'Recipients can only view messages through secure portal- no forwarding allowed',
    },
    {
      icon: Mail,
      title: 'Outlook Integration',
      description: '"Send Securely" button right in Outlook, zero friction for users',
    },
    {
      icon: Clock,
      title: 'Message Expiration',
      description: 'Set automatic expiration timers. messages self-destruct when time expires',
    },
    {
      icon: Globe,
      title: 'Custom Domains',
      description: 'White-label with your company domain (e.g., mail.yourcompany.com)',
    },
    {
      icon: Zap,
      title: 'No Account Needed',
      description: 'Recipients view messages instantly and no signup or complex setup required',
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Share securely with multiple team members with role-based permissions',
    },
  ];

  const navItems = [
    { href: '#features', label: 'Features', Icon: Zap },
    { href: '#about', label: 'About', Icon: Users },
    { href: '#security', label: 'Security', Icon: Shield },
    { href: '#pricing', label: 'Pricing', Icon: Globe },
    { href: '#faq', label: 'FAQ', Icon: ChevronDown },
  ];

  useEffect(() => {
    // First, try to load all image files from `src/images` using webpack's require.context
    // This will include any screenshots you keep in src/images and bundle them for dev/prod.
    try {
      // eslint-disable-next-line global-require, import/no-dynamic-require
      const req = require.context('../images', false, /\.(png|jpe?g|gif|webp|svg)$/);
      const imgs = req.keys().map((k) => {
        const mod = req(k);
        return mod.default || mod;
      });
      if (imgs.length) {
        // prefer secureImage2 as the header/outlook icon if present
        const preferred = imgs.find((u) => u.toString().includes('secureImage2')) || imgs[0];
        if (preferred) setOutlookIcon(preferred);
      }
    } catch (e) {
      // ignore: proceed to public/assets/static fallbacks
    }

    const publicBase = process.env.PUBLIC_URL || '';

    // Fallback: if assets weren't found in src/images, probe public/assets paths
    // (useful if you prefer placing assets in public instead).
    // outlook icon - try .png first, then .svg
    if (!outlookIcon) {
      fetch(`${publicBase}/assets/outlook-icon.png`, { method: 'HEAD' })
        .then((res) => {
          if (res.ok) setOutlookIcon(`${publicBase}/assets/outlook-icon.png`);
          else {
            fetch(`${publicBase}/assets/outlook-icon.svg`, { method: 'HEAD' })
              .then((res2) => {
                if (res2.ok) setOutlookIcon(`${publicBase}/assets/outlook-icon.svg`);
              })
              .catch(() => {
                setOutlookIcon(null);
              });
          }
        })
        .catch(() => {
          setOutlookIcon(null);
        });
    }

    // demo video fallback
    if (!demoSrc) {
      fetch(`${publicBase}/assets/demo.mp4`, { method: 'HEAD' })
        .then((res) => {
          if (res.ok) setDemoSrc(`${publicBase}/assets/demo.mp4`);
          else setDemoSrc('https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4');
        })
        .catch(() => {
          setDemoSrc('https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4');
        });
    }

  // no screenshot gallery; fallbacks remain for outlook icon and demo video
  }, [demoSrc, outlookIcon]);

  // show / hide scroll-to-top button
  useEffect(() => {
    const onScroll = () => {
      setShowScroll(window.pageYOffset > SCROLL_THRESHOLD);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // initialize
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [SCROLL_THRESHOLD]);

  // detect user's preferred color scheme and respond to changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const setFromMQ = (e) => setIsDarkTheme(e.matches ?? mq.matches);
    setIsDarkTheme(mq.matches);
    // handle both addEventListener and deprecated addListener
    if (mq.addEventListener) mq.addEventListener('change', setFromMQ);
    else mq.addListener(setFromMQ);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', setFromMQ);
      else mq.removeListener(setFromMQ);
    };
  }, []);

  // Intersection Observer for animations on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );

    const trustSection = document.querySelector('.trust-section');
    const sectionHeaders = document.querySelectorAll('.section-header');
    const finalCtaButtons = document.querySelector('.final-cta-buttons');
    
    if (trustSection) {
      observer.observe(trustSection);
    }
    
    sectionHeaders.forEach((header) => {
      observer.observe(header);
    });
    
    if (finalCtaButtons) {
      observer.observe(finalCtaButtons);
    }

    return () => {
      if (trustSection) {
        observer.unobserve(trustSection);
      }
      sectionHeaders.forEach((header) => {
        observer.unobserve(header);
      });
      if (finalCtaButtons) {
        observer.unobserve(finalCtaButtons);
      }
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const { name, email, message } = contactForm;
    const mailtoLink = `mailto:contactus-ssp@securesendpro.com?subject=Contact from ${encodeURIComponent(name)}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`)}`;
    window.location.href = mailtoLink;
    setFormStatus('Opening your email client...');
    setTimeout(() => {
      setContactForm({ name: '', email: '', message: '' });
      setFormStatus('');
    }, 2000);
  };

  // Previously we locked body scrolling when the mobile menu opened.
  // That behavior made the whole page feel "locked" under a gray cover.
  // Intentionally do not modify document.body.style.overflow so the page remains interactive
  // while the menu is open. Closing is still available via the toggle/X button.
  useEffect(() => {
    // no-op: do not change body overflow to avoid locking the page behind the menu
    return undefined;
  }, [mobileNavOpen]);

  const useCases = [
    {
      title: 'Banks & Financial Services',
      description: 'Send sensitive account statements, wire instructions, and compliance docs securely',
      icon: '🏦',
    },
    {
      title: 'Law Firms',
      description: 'Share confidential legal documents with clients and opposing counsel safely',
      icon: '⚖️',
    },
    {
      title: 'Healthcare',
      description: 'Secure sharing of patient records, lab results, and medical reports',
      icon: '🏥',
    },
    {
      title: 'HR & Recruitment',
      description: 'Securely send offer letters, contracts, and sensitive employee documents',
      icon: '👔',
    },
    {
      title: 'Insurance',
      description: 'Share claims, policy details, and risk assessments with verified recipients',
      icon: '📋',
    },
    {
      title: 'Government',
      description: 'Comply with security protocols for official correspondence and sensitive data',
      icon: '🏛️',
    },
  ];

  const faqItems = [
    {
      question: 'Do recipients need to create an account?',
      answer:
        'No. Recipients receive a secure link via email, enter their OTP, and can instantly view your message without any account creation.',
    },
    {
      question: 'Is the encryption truly end-to-end?',
      answer:
        "Yes. Messages are encrypted on the sender's device using RSA + AES, transmitted encrypted, and only decrypted by the recipient. Even our servers cannot read the content.",
    },
    {
      question: 'Can recipients forward messages?',
      answer:
        'No. Our secure portal prevents forwarding, copying, and unauthorized sharing. Only the verified recipient can view.',
    },
    {
      question: 'Does the Outlook add-in work on Mac?',
      answer:
        'Yes, SecureSendPro integrates with Outlook on Windows, Mac, and web. Simply click "Send Securely" from the ribbon.',
    },
    {
      question: 'How long do messages stay encrypted?',
      answer:
        'You can set custom expiration timers. After the expiration date, messages are permanently deleted and cannot be accessed by anyone.',
    },
    // {
    //   question: 'What compliance standards does SecureSendPro meet?',
    //   answer:
    //     'We are GDPR compliant. All data is encrypted at rest and in transit, and we perform regular security audits.',
    // },
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Chief Compliance Officer, Sterling Bank',
      text: 'SecureSendPro transformed how we handle sensitive communications. Our clients appreciate the transparency, and our compliance team sleeps better knowing communications are secure and compliant.',
      image: '👩‍⚖️',
    },
    {
      name: 'James Chen',
      role: 'Managing Partner, Chen & Associates Law Firm',
      text: 'The Outlook integration is seamless. Our attorneys simply click "Send Securely"—no need for training. The solution is reliable and integrates smoothly into our workflow.',
      image: '👨‍💼',
    },
    {
      name: 'Dr. Aisha Patel',
      role: 'Director of Patient Services, Metro Health Clinic',
      text: 'Data compliance was our top concern. SecureSendPro gives us the security and peace of mind we need to share patient records safely.',
      image: '👩‍⚕️',
    },
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <div>
              <img src={process.env.PUBLIC_URL + '/assets/outlook-icon.png'} alt="Outlook Icon" className="outlook-icon" />
              <span className="logo-text">SecureSendPro</span>
            </div>
            <div className="brand">A <span className="brand-highlight">DigitalPenPro</span> Product</div>
          </div>

          {/* mobile nav toggle (visible on small screens) */}
          <button
            className="mobile-nav-toggle"
            aria-controls="main-nav"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((s) => !s)}
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileNavOpen ? (
              <X />
            ) : (
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <rect width="22" height="2" rx="1" fill="currentColor" />
                <rect y="7" width="22" height="2" rx="1" fill="currentColor" />
                <rect y="14" width="22" height="2" rx="1" fill="currentColor" />
              </svg>
            )}
          </button>

          <nav id="main-nav" className={`nav nav-sheet ${mobileNavOpen ? 'open' : ''}`} aria-hidden={!mobileNavOpen}>
            {/* in-sheet close button for easier dismissal on mobile */}
            {mobileNavOpen && (
              <button
                className="nav-sheet-close"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
              >
                <X />
              </button>
            )}
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="nav-item"
                onClick={() => setMobileNavOpen(false)}
              >
                <item.Icon className="nav-item-icon" />
                <span className="nav-item-label">{item.label}</span>
              </a>
            ))}
          </nav>
          {/* <button className="btn btn-primary">Sign In</button> */}
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/assets/heroSecureSendBG.png)` }}
      >
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Send Confidential<br />Messages with<br />Bank-Grade Security</h1>
        </div>
         <div className="hero-subtitle">SecureSendPro combines end-to-end encryption and identity verification—integrated seamlessly into Outlook. Send with confidence knowing messages are encrypted and recipients are verified.</div>
         <div className="hero-cta">
              <button className="btn btn-secondary btn-large">Watch Demo</button>
        </div>
         <div className="hero-trust"><span className="trust-check">✓</span> Bank-grade encryption &nbsp;•&nbsp; <span className="trust-check">✓</span> Zero account required for recipients &nbsp;•&nbsp; <span className="trust-check">✓</span> Regular security audits</div>
    </div>
      </section>

      {/* mobile nav backdrop (closes menu when tapped) */}
      <div
        className={`mobile-nav-backdrop ${mobileNavOpen ? 'show' : ''}`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />

      {/* Demo Modal */}
      {showDemo && (
        <div className="demo-modal" role="dialog" aria-modal="true">
          <div className="demo-modal-backdrop" onClick={() => setShowDemo(false)} />
          <div className="demo-modal-content">
            <button className="demo-close" onClick={() => setShowDemo(false)} aria-label="Close demo">
              <X />
            </button>
            <div className="demo-video-wrapper">
              <video src={demoSrc} controls className="demo-video">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <Zap className="trust-icon" />
              <span>RSA + AES Encryption</span>
            </div>
            <div className="trust-item">
              <Users className="trust-icon" />
              <span>Trusted by 10+ Companies</span>
            </div>
            {/* <div className="trust-item">
              <Shield className="trust-icon" />
              <span>GDPR compliant</span>
            </div> */}
            <div className="trust-item">
              <Mail className="trust-icon" />
              <span>Zero-Knowledge Architecture</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Powerful Features Built for Security</h2>
            <p>Everything you need to send sensitive information safely</p>
          </div>
          <div className="features-grid">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="feature-card">
                  <div className="feature-icon">
                    {feature.title === 'Outlook Integration' ? (
                      <img
                        src={outlookIcon || `${process.env.PUBLIC_URL || ''}/assets/outlook-icon.png`}
                        alt="Outlook add-in"
                        className="feature-outlook-icon"
                      />
                    ) : (
                      <Icon size={28} />
                    )}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About (inline) - compact mission/vision/values */}
      <section id="about" className="about-inline">
        <div className="container">
          <div className="section-header">
            <h2>About SecureSendPro</h2>
            <p><b>A DigitalPenPro product </b> for secure business communication.</p>
            <p>Simple, secure messaging- built for teams that handle sensitive information.</p>
            <p>We help organizations share confidential data safely with Outlook integration, strong recipient verification, and enterprise auditing. Designed for healthcare, legal, finance and other security-conscious teams.</p>
          </div>

          <div className="about-grid">
            <div className="about-card">
              <div className="about-icon">🎯</div>
              <h4>Mission</h4>
              <p>Make secure communication effortless and reliable for organizations.</p>
            </div>

            <div className="about-card">
              <div className="about-icon">🌍</div>
              <h4>Vision</h4>
              <p>A world where every sensitive message is protected by default.</p>
            </div>

            <div className="about-card">
              <div className="about-icon">🔐</div>
              <h4>Security</h4>
              <p>Bank-grade encryption and identity verification for every message.</p>
            </div>

            <div className="about-card">
              <div className="about-icon">✨</div>
              <h4>Simplicity</h4>
              <p>Outlook-integrated workflows so teams can send securely without friction.</p>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <h3>Get in Touch</h3>
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  rows="5"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary">Send Message</button>
              {formStatus && <p className="form-status">{formStatus}</p>}
            </form>
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How SecureSendPro Works</h2>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h4>Compose & Encrypt</h4>
              <p>Write your message in Outlook or web portal. SecureSendPro encrypts it instantly.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h4>Recipient Verification</h4>
          <p>Recipient receives a secure link. They verify with OTP via email to prove identity.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h4>Secure Delivery</h4>
              <p>Message opens only for the verified recipient. You receive a delivery confirmation so you know the message was delivered and viewed.</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h4>Secure Reply (Optional)</h4>
              <p>Recipient can reply encrypted directly from portal. no Outlook needed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      {/* <section className="use-cases">
        <div className="container">
          <div className="section-header">
            <h2>Built for Your Industry</h2>
            <p>SecureSendPro serves the most security-conscious organizations</p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, idx) => (
              <div key={idx} className="use-case-card">
                <div className="use-case-emoji">{useCase.icon}</div>
                <h4>{useCase.title}</h4>
                <p>{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Product screenshots removed per request */}

      {/* Security Section */}
      <section id="security" className="security-section">
        <div className="container">
          <div className="section-header">
            <h2>Security Built Into Every Layer</h2>
            <p>Enterprise-grade protection you can trust</p>
          </div>
          <div className="security-grid">
            <div className="security-card">
              <h4>🔐 Encryption</h4>
              <ul>
                <li>RSA-4096 for key exchange</li>
                <li>AES-256 for message encryption</li>
                <li>End-to-end encryption</li>
                <li>Encrypted at rest & in transit</li>
              </ul>
            </div>
            <div className="security-card">
              <h4>🪪 Identity & Access</h4>
              <ul>
                <li>OTP verification via email</li>
                <li>JWT-based secure tokens</li>
                <li>Session timeout controls</li>
                <li>Multi-tenant data isolation</li>
              </ul>
            </div>
            <div className="security-card">
              <h4>📊 Compliance</h4>
              <ul>
                <li>Regular security audits</li>
                <li>Data Residency Options</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Trusted by Industry Leaders</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="testimonial-card">
                <div className="testimonial-avatar">{testimonial.image}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <p className="author-name">{testimonial.name}</p>
                  <p className="author-role">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            {/* <p>Choose the plan that fits your organization</p> */}
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
             <div className="badge">Most Popular</div>
              <h4>Enterprise</h4>
              <div className="price">
                <span className="amount">Custom</span>
              </div>
              <ul className="pricing-features">
                <li>✓ Everything in Pro</li>
                <li>✓ Unlimited users</li>
                <li>✓ Custom domain</li>
                <li>✓ Dedicated support</li>
                <li>✓ SLA guarantee</li>
              </ul>
              <a href="mailto:contactus-ssp@securesendpro.com" className="btn btn-secondary btn-large">Contact Sales</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faqItems.map((item, idx) => (
              <div key={idx} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setExpandedFAQ(expandedFAQ === idx ? null : idx)}
                >
                  <span>{item.question}</span>
                  <ChevronDown
                    size={20}
                    className={expandedFAQ === idx ? 'expanded' : ''}
                  />
                </button>
                {expandedFAQ === idx && <div className="faq-answer">{item.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Send Secure Messages?</h2>
          <p>Join 10+ organizations protecting their most sensitive communications</p>
          <div className="final-cta-buttons">
            {/* <button className="btn btn-primary btn-large">Send Your First Message</button> */}
            <button className="btn btn-secondary btn-large">Request a Demo</button>
          </div>
        </div>
      </section>

      {/* Screenshot modal removed */}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>SecureSendPro</h4>
              <p>Enterprise-grade secure email for organizations that can't take risks.</p>
            </div>
            <div className="footer-section">
              <h5>Product</h5>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#security">Security</a></li>
                <li><a href="#pricing">Pricing</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h5>Company</h5>
              <ul>
                <li><a href="/about">About</a></li>
                <li><a href="#blog">Blog</a></li>
                <li><a href="mailto:contactus-ssp@securesendpro.com">Contact</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h5>Legal</h5>
              <ul>
                <li><a href="/TermsOfService">Terms of Service</a></li>
                <li><a href="/PrivacyPolicy">Privacy Policy</a></li>
                <li><a href="#compliance">Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} SecureSendPro. All rights reserved.</p>
            <div className="social-links">
              <a href="#twitter">Twitter</a>
              <a href="#linkedin">LinkedIn</a>
              <a href="#github">GitHub</a>
            </div>
          </div>
        </div>
      <div class="dpp-ss-footer">
        A <a href="https://digitalpenpro.com" target="_blank">DigitalPenPro</a> Product
      </div>
      </footer>
      {/* Scroll to top button */}
      <button
        className={`scroll-top ${showScroll ? 'show' : ''} ${isDarkTheme ? 'dark' : 'light'}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
        title="Back to top"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}

export default LandingPage;
